/* ============================================================================
 * milliDepartments.js — the 16 desks Milli answers from, for the dashboard.
 *
 * Mirror of supabase/functions/milli-chat/departments.ts (ids, names,
 * pillars, colors). The server is the source of truth: every chat reply
 * carries its own `department` payload, and run rows carry
 * plan.department. This catalog only exists so the console can color a
 * run, a legend or an accent without a round trip. Keep the two in step
 * when a desk is added or recolored.
 * ========================================================================== */

/** Milli's own copper, worn when no desk has spoken yet. */
export const MILLI_COPPER = '#c9873e';

export const PILLARS = [
  { id: 'operations_growth', name: 'Operations / Growth', departments: ['operations', 'sales', 'client_relations', 'estimating'] },
  { id: 'content_marketing', name: 'Content / Marketing', departments: ['marketing', 'content', 'social', 'communications'] },
  { id: 'product_design', name: 'Product / Design', departments: ['product', 'engineering', 'design', 'research', 'formquick'] },
  { id: 'back_office_finance', name: 'Back Office / Finance', departments: ['finance', 'legal', 'people'] },
];

export const DEPARTMENTS = {
  operations: { id: 'operations', name: 'Operations', pillar: 'Operations / Growth', uiColor: 'amber-500', hex: '#f59e0b' },
  sales: { id: 'sales', name: 'Sales', pillar: 'Operations / Growth', uiColor: 'emerald-500', hex: '#10b981' },
  client_relations: { id: 'client_relations', name: 'Client Relations', pillar: 'Operations / Growth', uiColor: 'teal-500', hex: '#14b8a6' },
  estimating: { id: 'estimating', name: 'Estimating', pillar: 'Operations / Growth', uiColor: 'orange-500', hex: '#f97316' },
  marketing: { id: 'marketing', name: 'Marketing', pillar: 'Content / Marketing', uiColor: 'rose-500', hex: '#f43f5e' },
  content: { id: 'content', name: 'Content', pillar: 'Content / Marketing', uiColor: 'violet-500', hex: '#8b5cf6' },
  social: { id: 'social', name: 'Social', pillar: 'Content / Marketing', uiColor: 'fuchsia-500', hex: '#d946ef' },
  communications: { id: 'communications', name: 'Communications', pillar: 'Content / Marketing', uiColor: 'pink-500', hex: '#ec4899' },
  product: { id: 'product', name: 'Product', pillar: 'Product / Design', uiColor: 'sky-500', hex: '#0ea5e9' },
  engineering: { id: 'engineering', name: 'Engineering', pillar: 'Product / Design', uiColor: 'indigo-500', hex: '#6366f1' },
  design: { id: 'design', name: 'Design', pillar: 'Product / Design', uiColor: 'yellow-500', hex: '#eab308' },
  research: { id: 'research', name: 'Research', pillar: 'Product / Design', uiColor: 'cyan-500', hex: '#06b6d4' },
  formquick: { id: 'formquick', name: 'FormQuick', pillar: 'Product / Design', uiColor: 'purple-500', hex: '#7B4FC0' },
  finance: { id: 'finance', name: 'Finance', pillar: 'Back Office / Finance', uiColor: 'lime-500', hex: '#84cc16' },
  legal: { id: 'legal', name: 'Legal & Compliance', pillar: 'Back Office / Finance', uiColor: 'slate-400', hex: '#94a3b8' },
  people: { id: 'people', name: 'People & Admin', pillar: 'Back Office / Finance', uiColor: 'stone-400', hex: '#a8a29e' },
};

export const departmentById = (id) => (id && DEPARTMENTS[id]) || null;

/** The desk a kc_agent_runs row was filed under (plan.department), or null
 * for runs from before routing existed and for non-chat runs. */
export const departmentFromRun = (run) => departmentById(run?.plan?.department);

/** The accent a surface should wear for a department payload (from a chat
 * reply) — Milli's copper when there is none. */
export const accentFor = (department) => department?.hex || MILLI_COPPER;
